import ComparisonIcon from '../components/ui/ComparisonIcon.jsx';
import Eyebrow from '../components/ui/Eyebrow.jsx';
import TodayIcon from '../components/ui/TodayIcon.jsx';
import { COMPARISON } from '../content/home.js';

/**
 * What Chickrah replaces, set against how the job is done today.
 *
 * Two presentations of the same data rather than one table forced to scroll
 * sideways on a phone: a real <table> from `sm` up, and stacked cards below it.
 * A 3-column table at 375px is unreadable however it is scrolled.
 *
 * Deliberately a LIGHT panel. It sits between two dark sections in the page
 * order, and as a dark card it merged into whichever one preceded it — two dark
 * blocks with a hairline of cream between them read as one broken section.
 */
export default function ComparisonTable() {
  return (
    <section className="py-16 lg:py-20">
      <div className="wrap">
        <div className="overflow-hidden rounded-3xl border border-line bg-white shadow-card">
          <header className="px-5 pt-9 text-center sm:px-10 lg:pt-12">
            <Eyebrow className="mb-2.5">{COMPARISON.eyebrow}</Eyebrow>
            <h2 className="mx-auto mb-3 max-w-[560px] font-display text-[22px] font-bold text-deep lg:text-[27px]">
              {COMPARISON.heading.left}{' '}
              <span className="text-amber">{COMPARISON.heading.vs}</span>{' '}
              {COMPARISON.heading.right}
            </h2>
            <p className="mx-auto max-w-[520px] text-[14.5px] leading-relaxed text-muted">
              {COMPARISON.sub}
            </p>
          </header>

          {/* Phones: one card per row. Both states stay labelled, because once
              the table header is gone an icon and a tick with nothing naming
              them is meaningless. */}
          <ul className="space-y-3 px-5 py-8 sm:hidden">
            {COMPARISON.rows.map((row) => (
              <li key={row.feature} className="rounded-2xl border border-line bg-cream p-4">
                <div className="mb-3 flex items-center gap-3">
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-deep text-amber">
                    <ComparisonIcon name={row.icon} className="h-[18px] w-[18px]" />
                  </span>
                  <h3 className="font-display text-[14.5px] font-bold leading-tight text-deep">
                    {row.feature}
                  </h3>
                </div>

                <dl className="space-y-2 text-[13px]">
                  <div className="flex items-center gap-2">
                    <dt className="flex-none font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
                      Today
                    </dt>
                    <dd className="ml-auto flex items-center gap-2 text-right text-muted">
                      <span>{row.replaces}</span>
                      <span className="grid h-7 w-7 flex-none place-items-center rounded-lg border border-line bg-white text-muted">
                        <TodayIcon name={row.todayIcon} className="h-3.5 w-3.5" />
                      </span>
                    </dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <dt className="flex-none font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
                      Chickrah
                    </dt>
                    <dd className="ml-auto flex items-center gap-2 text-right font-semibold text-deep">
                      <span>Built in</span>
                      <TickMark />
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>

          {/* Tablet and up: the table */}
          <div className="hidden px-5 py-8 sm:block sm:px-10 lg:py-10">
            <table className="w-full border-collapse">
              <caption className="sr-only">
                What Chickrah replaces, compared with manual record keeping
              </caption>
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="w-[46%] px-4 pb-3.5 text-left font-mono text-[10.5px] uppercase tracking-[0.15em] text-muted"
                  >
                    {COMPARISON.columns[0]}
                  </th>
                  <th
                    scope="col"
                    className="w-[38%] px-4 pb-3.5 text-left font-mono text-[10.5px] uppercase tracking-[0.15em] text-muted"
                  >
                    {COMPARISON.columns[1]}
                  </th>
                  <th
                    scope="col"
                    className="px-4 pb-3.5 text-center font-mono text-[10.5px] uppercase tracking-[0.15em] text-field"
                  >
                    {COMPARISON.columns[2]}
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.rows.map((row, i) => (
                  <tr key={row.feature} className={i % 2 ? 'bg-cream' : 'bg-transparent'}>
                    <th scope="row" className="rounded-l-xl px-4 py-3 text-left font-normal">
                      <span className="flex items-center gap-3">
                        <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-deep text-amber">
                          <ComparisonIcon name={row.icon} className="h-[18px] w-[18px]" />
                        </span>
                        <span className="font-display text-[13.5px] font-semibold text-deep">
                          {row.feature}
                        </span>
                      </span>
                    </th>

                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2.5 text-[13.5px] text-muted">
                        <span className="grid h-8 w-8 flex-none place-items-center rounded-lg border border-line bg-white text-muted">
                          <TodayIcon name={row.todayIcon} className="h-4 w-4" />
                        </span>
                        {row.replaces}
                      </span>
                    </td>

                    <td className="rounded-r-xl px-4 py-3 text-center align-middle">
                      <TickMark className="mx-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* A rule rather than a call to action. The pricing section follows
              immediately with its own buttons, so a CTA here interrupted the
              read for no gain; this just closes the panel off. */}
          <div aria-hidden="true" className="h-1.5 w-full bg-field" />
        </div>
      </div>
    </section>
  );
}

/**
 * Solid fill, white glyph. A 10%-tint disc with a dark tick inside reads as a
 * disabled control rather than a confirmation — the affirmative mark has to be
 * the strongest thing in its column, and it is the only saturated colour in
 * the panel so the eye lands on it.
 */
function TickMark({ className = '' }) {
  return (
    <span
      className={`grid h-[26px] w-[26px] place-items-center rounded-full bg-field text-white shadow-[0_2px_6px_rgba(31,77,44,0.30)] ${className}`}
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-3.5 w-3.5"
        aria-hidden="true"
      >
        <path d="M4.5 10.5l3.5 3.5 7.5-8" />
      </svg>
      <span className="sr-only">Included</span>
    </span>
  );
}
